import React, { useEffect, useRef, useState } from 'react';
import MealPlannerWizard from '../../components/MealPlanner/MealPlannerWizard';
import ShoppingList from '../../components/MealPlanner/ShoppingList';
import WeeklyDashboard from '../../components/MealPlanner/WeeklyDashboard';
import { generateBudgetMealPlan } from '../../utils/MealPlanner/optimizer';
import { fetchMealPlannerProducts, ProductFeedResult } from '../../utils/MealPlanner/productApi';
import { sampleProducts, sampleRecipes } from '../../utils/MealPlanner/sampleData';
import {
  GeneratedMealPlan,
  MealPlannerPreferences,
  UserInventoryItem,
} from '../../utils/MealPlanner/types';
import './MealPlanner.css';

const storageKey = 'budget-meal-planner';

const defaultPreferences: MealPlannerPreferences = {
  weeklyBudgetCap: 50,
  storeId: 'tesco',
  people: 2,
  dietaryRestrictions: [],
  cookingStyle: 'leftover-rotation',
};

type StoredPlannerState = {
  preferences: MealPlannerPreferences;
  inventory: UserInventoryItem[];
};

function loadPlannerState(): StoredPlannerState {
  if (typeof window === 'undefined') {
    return { preferences: defaultPreferences, inventory: [] };
  }

  try {
    const rawState = window.localStorage.getItem(storageKey);
    if (!rawState) {
      return { preferences: defaultPreferences, inventory: [] };
    }

    const parsed = JSON.parse(rawState) as Partial<StoredPlannerState>;

    return {
      preferences: { ...defaultPreferences, ...parsed.preferences },
      inventory: Array.isArray(parsed.inventory) ? parsed.inventory : [],
    };
  } catch {
    return { preferences: defaultPreferences, inventory: [] };
  }
}

function MealPlanner() {
  const initialState = loadPlannerState();
  const [preferences, setPreferences] = useState<MealPlannerPreferences>(initialState.preferences);
  const [inventory, setInventory] = useState<UserInventoryItem[]>(initialState.inventory);
  const [plan, setPlan] = useState<GeneratedMealPlan | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [productFeed, setProductFeed] = useState<ProductFeedResult | null>(null);
  const [generationMessage, setGenerationMessage] = useState('');
  const resultsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    window.localStorage.setItem(storageKey, JSON.stringify({ preferences, inventory }));
  }, [preferences, inventory]);

  async function generatePlan() {
    setIsGenerating(true);
    setGenerationMessage('Generating your weekly plan...');

    try {
      const feed = await fetchMealPlannerProducts(preferences.storeId, sampleRecipes);
      const products = feed.products.length > 0 ? feed.products : sampleProducts;
      setProductFeed(feed);
      setPlan(generateBudgetMealPlan(sampleRecipes, products, inventory, preferences));
      setGenerationMessage(feed.provider === 'fallback' ? 'Plan generated with fallback prices.' : 'Plan generated with product feed prices.');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to load product feed';
      setProductFeed({
        products: sampleProducts,
        warnings: [`Live product feed unavailable: ${message}. Seeded fallback prices were used.`],
        provider: 'fallback',
      });
      setPlan(generateBudgetMealPlan(sampleRecipes, sampleProducts, inventory, preferences));
      setGenerationMessage('Plan generated with fallback prices.');
    } finally {
      setIsGenerating(false);
      window.setTimeout(() => {
        resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 50);
    }
  }

  return (
    <section className="meal-planner-page">
      <div className="meal-planner-hero">
        <div>
          <p className="meal-planner-eyebrow">Automated budget planner</p>
          <h1>Budget Meal Planner</h1>
          <p>
            Build a weekly meal schedule from store prices, fridge inventory, and cooking style,
            then shop only the delta.
          </p>
        </div>
        <div className="meal-planner-cost-card" aria-label="Current budget summary">
          <span>Target cap</span>
          <strong>EUR {preferences.weeklyBudgetCap.toFixed(0)}</strong>
          <small>{preferences.people} people · {preferences.storeId.toUpperCase()}</small>
        </div>
      </div>

      <div className="meal-planner-layout">
        <MealPlannerWizard
          preferences={preferences}
          inventory={inventory}
          onPreferencesChange={setPreferences}
          onInventoryChange={setInventory}
          onGenerate={generatePlan}
          isGenerating={isGenerating}
        />
        <div className="meal-planner-results" ref={resultsRef}>
          {(generationMessage || productFeed) && (
            <div className="meal-planner-feed-status" role="status">
              <strong>
                {isGenerating
                  ? 'Generating plan'
                  : productFeed?.provider === 'fallback'
                    ? 'Fallback prices'
                    : 'Live product feed'}
              </strong>
              <span>
                {generationMessage}
                {productFeed ? ` · ${productFeed.products.length} products` : ''}
                {productFeed?.cache ? ` · cache ${productFeed.cache}` : ''}
              </span>
              {productFeed?.warnings[0] && <small>{productFeed.warnings[0]}</small>}
            </div>
          )}
          <WeeklyDashboard plan={plan} />
          <ShoppingList plan={plan} />
        </div>
      </div>
    </section>
  );
}

export default MealPlanner;
